import React, { useState } from "react";
import styled from "styled-components";
import Button from "../../components/common/Button";
import { ArrowDown } from "../../components/icons";
import { IrisLogo } from "../../components/icons/IrisLogo";
import { Loading } from "../../components/icons/Loading";
import { ModalContainer, ModalTitle } from "../../common/modal.styles";
import { useReservationResources } from "./hooks/useReservationResources";

export type SubmittingButton = "payNow" | "payLater" | "iris" | null;

export interface PaymentButtonsProps {
  onPayNow: () => void;
  onPayLater: () => void;
  onIris: () => void;
  isSubmitting?: boolean;
  submittingButton?: SubmittingButton;
  isFormDisabled?: boolean;
  isPayNowActive?: boolean;
  isPayLaterActive?: boolean;
  isIrisActive?: boolean;
  isFreeOrder?: boolean;
  hasSecondaryOptions?: boolean;
  alwaysExpanded?: boolean;
}

const MoreOptionsToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px;
`;

const RotatingArrow = styled.div<{ $expanded: boolean }>`
  display: flex;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(180deg)" : "rotate(0deg)"};
`;

const DrawerActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export function PaymentButtons({
  onPayNow,
  onPayLater,
  onIris,
  isSubmitting,
  submittingButton,
  isFormDisabled,
  isPayNowActive,
  isPayLaterActive,
  isIrisActive,
  isFreeOrder,
  hasSecondaryOptions = false,
  alwaysExpanded = false,
}: PaymentButtonsProps) {
  const r = useReservationResources();
  const [expanded, setExpanded] = useState(false);

  const showSecondary = alwaysExpanded || (hasSecondaryOptions && expanded);

  return (
    <>
      {isPayNowActive && (
        <Button
          id="pay-now-btn"
          type="button"
          variant="primary"
          text={isFreeOrder ? r.Continue : r.PayNow}
          height="48px"
          fullWidth
          onClick={onPayNow}
          disabled={isFormDisabled || isSubmitting}
          iconComponent={
            isSubmitting && submittingButton === "payNow" ? (
              <Loading />
            ) : undefined
          }
        />
      )}
      {!alwaysExpanded && hasSecondaryOptions && (
        <MoreOptionsToggleWrapper>
          <Button
            id="view-more-btn"
            type="button"
            variant="tertiary"
            text={r.PaymentOptions}
            onClick={() => setExpanded((v) => !v)}
            iconBefore={
              <RotatingArrow $expanded={expanded}>
                <ArrowDown />
              </RotatingArrow>
            }
          />
        </MoreOptionsToggleWrapper>
      )}
      {showSecondary && (
        <>
          {isPayLaterActive && (
            <Button
              id="pay-later-btn"
              type="button"
              variant="secondary"
              text={r.PayLater}
              height="48px"
              fullWidth
              onClick={onPayLater}
              disabled={isFormDisabled || isSubmitting}
              iconComponent={
                isSubmitting && submittingButton === "payLater" ? (
                  <Loading />
                ) : undefined
              }
            />
          )}
          {isIrisActive && (
            <Button
              id="pay-iris-btn"
              type="button"
              variant="secondary"
              text={r.InstantMoneyTransfer}
              height="48px"
              iconBefore={<IrisLogo />}
              fullWidth
              onClick={onIris}
              disabled={isFormDisabled || isSubmitting}
              iconComponent={
                isSubmitting && submittingButton === "iris" ? (
                  <Loading />
                ) : undefined
              }
            />
          )}
        </>
      )}
    </>
  );
}

export function PaymentModal({
  onPayNow,
  onPayLater,
  onIris,
  isSubmitting,
  submittingButton,
  isFormDisabled,
  isPayNowActive,
  isPayLaterActive,
  isIrisActive,
  isFreeOrder,
  hasSecondaryOptions,
}: PaymentButtonsProps) {
  const r = useReservationResources();
  return (
    <ModalContainer>
      <ModalTitle>{r.PaymentModalDescription}</ModalTitle>
      <DrawerActions>
        <PaymentButtons
          onPayNow={onPayNow}
          onPayLater={onPayLater}
          onIris={onIris}
          isSubmitting={isSubmitting}
          submittingButton={submittingButton}
          isFormDisabled={isFormDisabled}
          isPayNowActive={isPayNowActive}
          isPayLaterActive={isPayLaterActive}
          isIrisActive={isIrisActive}
          isFreeOrder={isFreeOrder}
          hasSecondaryOptions={hasSecondaryOptions}
          alwaysExpanded
        />
      </DrawerActions>
    </ModalContainer>
  );
}
